import { createTheme } from '@mui/material/styles';

// Brand gradient colors
const brandGradient = {
  colors: ['#000113', '#020219', '#240d6f', '#7047dc', '#e3d9fb'],
  // Gradient string for backgrounds
  backgroundGradient: 'linear-gradient(to bottom, #000113, #020219, #240d6f, #7047dc, #e3d9fb)',
  // Gradient string for buttons
  buttonGradient: 'linear-gradient(to right, #240d6f, #7047dc)',
  buttonHoverGradient: 'linear-gradient(to right, #1a095a, #5f3bc0)',
};

// Brand color palette
const brandColors = {
  primary: {
    main: '#7047dc',
    light: '#e3d9fb',
    dark: '#240d6f',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#e3d9fb',
    light: '#ffffff',
    dark: '#7047dc',
    contrastText: '#000113',
  },
};

// Glass effect styles
const glassEffect = {
  light: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.9)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  },
  dark: {
    backgroundColor: 'rgba(30, 30, 40, 0.75)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  }
};

// Light theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: brandColors.primary,
    secondary: brandColors.secondary,
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
      gradient: 'linear-gradient(to right, #e3d9fb, #FFFFFF)',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },
    brandGradient: brandGradient,
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          ...glassEffect.light,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        outlined: {
          ...glassEffect.light,
          border: '1px solid rgba(255, 255, 255, 0.9)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s ease',
        },
        containedPrimary: {
          background: brandGradient.buttonGradient,
          '&:hover': {
            background: brandGradient.buttonHoverGradient,
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 15px rgba(112, 71, 220, 0.4)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(to right, #000113, #240d6f)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(to bottom, #f8f9fa, #e3d9fb 100%)',
          borderRight: '1px solid rgba(255, 255, 255, 0.9)',
        },
      },
    },
  },
});

// Dark theme
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: brandColors.primary,
    secondary: brandColors.secondary,
    background: {
      default: '#000113',
      paper: '#020219',
      gradient: 'linear-gradient(to bottom, #000113, #020219, #240d6f)',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#e3d9fb',
    },
    brandGradient: brandGradient,
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          ...glassEffect.dark,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
        outlined: {
          ...glassEffect.dark,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s ease',
        },
        containedPrimary: {
          background: brandGradient.buttonGradient,
          '&:hover': {
            background: brandGradient.buttonHoverGradient,
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 15px rgba(112, 71, 220, 0.4)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(to right, #000113, #240d6f)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(to bottom, #000113, #020219, #240d6f 80%)',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
});
